import { authClient } from '@api/apiClient'
import { User } from '@features/auth/hooks/types'
import { TOKEN_KEY } from '@features/auth/utils/constants'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import * as Sentry from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import type { AxiosResponse } from 'axios'
import { isAxiosError } from 'axios'
import { jwtDecode } from 'jwt-decode'
import { useSnackbar } from 'notistack'
import type { FC, ReactNode } from 'react'
import { createContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface LoginFormValues {
  username: string
  password: string
}
interface LoginResponse {
  access_token: string
  token_type: string
}

type DecodedToken = {
  exp: number //unix time
}

const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

const checkTokenValidity = (logout: () => void) => {
  const token = localStorage.getItem(TOKEN_KEY)

  if (token) {
    try {
      const decodedToken = jwtDecode<DecodedToken>(token)
      const currentTime = Math.floor(Date.now() / 1000)

      if (decodedToken.exp && decodedToken.exp < currentTime) {
        logout()
      }
    } catch (error) {
      console.error('Invalid token: ', error)
      logout()
    }
  } else {
    logout()
  }
}

export interface AuthContextProps {
  user: User | null
  login: (data: LoginFormValues) => Promise<void>
  logout: () => void
  isLoadingAuth: boolean
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined)
export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { isLoading, isSuccess, isError, data } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await authClient.get<User>('/me', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
        },
      })
      return response.data
    },
    retry: false,
  })

  useEffect(() => {
    if (isSuccess && !!data) {
      setUser(data)
      if (location.pathname === '/login') {
        void navigate({ to: '/' })
      }
    }
  }, [data, isSuccess, location.pathname, navigate])

  useEffect(() => {
    if (isError) {
      setUser(null)
      setAuthToken(null)
    }
  }, [isError])

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginFormValues) => {
      const formData = new FormData()
      formData.append('username', credentials.username)
      formData.append('password', credentials.password)
      formData.append('grant_type', 'password')

      const response = await authClient.post<LoginFormValues, AxiosResponse<LoginResponse>>('/token', formData)
      return response.data
    },
    onSuccess: async (response, variables) => {
      setAuthToken(response.access_token)
      await queryClient.invalidateQueries({ queryKey: ['user'] })
      enqueueSnackbar(t('login_page.login_message'), { variant: 'success' })
      Sentry.setUser({ username: variables?.username })
      Sentry.setTag('domain', window?.location?.href || 'no window')

      await navigate({ to: '/' })
    },
    onError: (error) => {
      console.log('error', error)
      if (isAxiosError(error) && error.response?.status === 401) {
        enqueueSnackbar(t('login_page.login_error'), { variant: 'error' })
      } else {
        enqueueSnackbar(t('login_page.error'), { variant: 'error' })
      }
    },
  })

  const login = async (requestData: LoginFormValues) => {
    await loginMutation.mutateAsync(requestData)
  }

  const logout = async () => {
    setUser(null)
    setAuthToken(null)
    queryClient.clear()
    enqueueSnackbar(t('login_page.logout_message'), { variant: 'info' })
    await navigate({ to: '/login' })
  }

  useEffect(() => {
    if (user) {
      const intervalId = setInterval(() => checkTokenValidity(logout), 5000)

      return () => clearInterval(intervalId)
    }
    return
  }, [user])

  const isLoadingAuth: boolean = isLoading || (isSuccess && !user) || !!(isError && user) || loginMutation.isPending

  if (isLoading || (isSuccess && !user) || (isError && user)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress size={40} />
      </Box>
    )
  }

  return <AuthContext.Provider value={{ user, login, logout, isLoadingAuth }}>{children}</AuthContext.Provider>
}
